import type { InsightBlock } from '@/shared/api/modules/facilities';
import { Icon } from '@/shared/assets/icons';
import { cn } from '@/shared/lib/cn';
import { Tag, Typography } from '@/shared/ui';
import type { FC } from 'react';
import { renderRichText } from '../lib/render-rich-text';
import { CATEGORY_STYLES } from '../model/category';

type InsightCardProps = {
  block: InsightBlock;
};

/**
 * One insight block. Figma 248:20016 / 248:20034 / 248:20056.
 *
 * Rounded on the left only: the card bleeds into the panel's right edge, which
 * is flush with the viewport.
 *
 * Every optional field is rendered only when present — the server already
 * strips the ones that do not belong to the category, and a missing field must
 * never surface as an empty heading or an "N/A" row.
 */
export const InsightCard: FC<InsightCardProps> = ({ block }) => {
  const style = CATEGORY_STYLES[block.category];
  const hasActionPlan = Boolean(block.action_plan?.length);

  return (
    <div
      data-testid="ai-insight-block"
      data-category={block.category}
      className={cn('relative overflow-hidden rounded-l-[10px]', style.surface)}
    >
      <div className={cn('absolute inset-y-0 left-0 w-1', style.accent)} />

      <div className="flex flex-col gap-3 px-[21px] py-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Icon
              name={style.glyphIcon}
              {...style.glyphSize}
              className={cn('shrink-0', style.glyph)}
            />
            <Typography as="span" weight="medium" className="text-neutral-12">
              {block.title}
            </Typography>
          </div>
          <Tag
            text={style.label}
            className={cn('shrink-0', style.tagClassName)}
          />
        </div>

        <Typography as="p" className="text-neutral-12">
          {renderRichText(block.body, { colorDeltas: true })}
        </Typography>

        {block.why_it_happens && (
          <div className="bg-neutral-1 border-insight-why-border flex flex-col gap-2 rounded-[10px] border p-3">
            <Typography
              as="span"
              weight="medium"
              className="text-insight-why-label"
            >
              Why it&apos;s happening
            </Typography>
            <Typography as="p" className="text-neutral-12">
              {renderRichText(block.why_it_happens)}
            </Typography>
          </div>
        )}

        {hasActionPlan && (
          <div className="flex flex-col gap-2">
            <Typography
              as="span"
              weight="medium"
              className="text-insight-heading"
            >
              What to do - next 90 days
            </Typography>
            <ol className="text-neutral-12 list-decimal ps-5">
              {block.action_plan!.map((step, index) => (
                <li
                  className="text-sm/[22px]"
                  key={`${block.id}-step-${index}`}
                >
                  <span className="font-semibold">{step.timeframe}:</span>{' '}
                  {/*
                   * Action text goes through the same renderer as every other
                   * field. It used to be printed raw, which was invisible while
                   * actions were generic prose — but the prompt now asks them to
                   * name concrete facilities, and the model bolds those names,
                   * so raw output showed literal "**Renaissance**" on screen.
                   */}
                  {renderRichText(step.action)}
                </li>
              ))}
            </ol>
          </div>
        )}

        {block.recommended && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Icon
                name="insight-recommended"
                className="text-insight-heading shrink-0"
                width={14}
                height={23}
              />
              <Typography
                as="span"
                weight="medium"
                className="text-insight-heading"
              >
                Recommended
              </Typography>
            </div>
            <Typography as="p" className="text-neutral-12">
              {renderRichText(block.recommended)}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};
